import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useCall } from "@/context/CallContext";
import { toast } from "sonner";
import { ChatCircleText, Phone, VideoCamera, DotsThreeVertical, SealCheck, HeartBreak, Prohibit, Warning, ChatsCircle, CalendarPlus } from "@phosphor-icons/react";
import { mediaUrl } from "@/pages/match/MatchOnboarding";

export default function MatchMatches() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const call = useCall();
  const [menu, setMenu] = useState(null);
  const [report, setReport] = useState(null);
  const [dateModal, setDateModal] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["match-matches"], queryFn: async () => (await api.get("/match/matches")).data });

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body)).data,
    onSuccess: (_r, v) => {
      toast.success(v.msg || "Done");
      qc.invalidateQueries({ queryKey: ["match-matches"] });
      setMenu(null); setReport(null);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading) return <div className="py-16 text-center text-muted-foreground">Loading matches…</div>;
  const matches = data?.matches || [];

  if (!matches.length) {
    return (
      <div className="rounded-3xl border border-border bg-card p-10 text-center" data-testid="match-matches-empty">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-muted-foreground"><ChatsCircle size={28} /></div>
        <h3 className="font-display text-lg font-semibold">No matches yet</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">When you and someone like each other, a private Match Room opens here with chat, voice and video.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="match-matches">
      {matches.map((m) => (
        <div key={m.user_id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`match-row-${m.user_id}`}>
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full bg-secondary">
            {m.photos?.length ? <img src={mediaUrl(m.photos[0].url)} alt={m.display_name} className="h-full w-full object-cover" />
              : <div className="flex h-full items-center justify-center text-xl font-display text-muted-foreground/50">{m.display_name?.charAt(0)}</div>}
            {m.online && <span className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 font-semibold">{m.display_name}, {m.age}{m.verified && <SealCheck size={14} weight="fill" className="text-sky-400" />}</p>
            <p className="truncate text-xs text-muted-foreground">{m.city ? `${m.city}${m.country ? ", " + m.country : ""} · ` : ""}<span className="capitalize">{m.intention}</span></p>
          </div>
          <div className="flex items-center gap-1">
            <IconBtn testid={`match-chat-${m.user_id}`} title="Message" onClick={() => navigate(`/app/text?with=${m.user_id}`)}><ChatCircleText size={19} weight="fill" /></IconBtn>
            <IconBtn testid={`match-voice-${m.user_id}`} title="Voice call" onClick={() => call?.startCall?.(m.user_id, false)}><Phone size={18} weight="fill" /></IconBtn>
            <IconBtn testid={`match-video-${m.user_id}`} title="Video date" onClick={() => call?.startCall?.(m.user_id, true)}><VideoCamera size={19} weight="fill" /></IconBtn>
            <IconBtn testid={`match-date-${m.user_id}`} title="Schedule a date" onClick={() => setDateModal(m)}><CalendarPlus size={19} weight="fill" /></IconBtn>
            <div className="relative">
              <IconBtn testid={`match-menu-${m.user_id}`} title="More" onClick={() => setMenu(menu === m.user_id ? null : m.user_id)}><DotsThreeVertical size={19} weight="bold" /></IconBtn>
              {menu === m.user_id && (
                <div className="absolute right-0 top-10 z-30 w-44 rounded-xl border border-border bg-card p-1.5 shadow-xl">
                  <MenuItem testid={`match-unmatch-${m.user_id}`} icon={HeartBreak} onClick={() => mut.mutate({ path: "/match/unmatch", body: { other_id: m.user_id }, msg: "Unmatched." })}>Unmatch</MenuItem>
                  <MenuItem testid={`match-block-${m.user_id}`} icon={Prohibit} danger onClick={() => mut.mutate({ path: "/match/block", body: { target_id: m.user_id }, msg: "Blocked." })}>Block</MenuItem>
                  <MenuItem testid={`match-report-${m.user_id}`} icon={Warning} danger onClick={() => { setReport(m); setMenu(null); }}>Report</MenuItem>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {dateModal && <DateScheduler match={dateModal} onClose={() => setDateModal(null)} />}

      {report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setReport(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-5" onClick={(e) => e.stopPropagation()} data-testid="match-report-modal">
            <h3 className="font-display text-lg font-semibold">Report {report.display_name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">Our team will review this. You can also block them.</p>
            <div className="mt-3 space-y-2">
              {[["inappropriate", "Inappropriate content"], ["fake", "Fake profile"], ["harassment", "Harassment"], ["spam", "Spam / scam"], ["other", "Other"]].map(([v, l]) => (
                <button key={v} data-testid={`report-reason-${v}`} onClick={() => mut.mutate({ path: "/match/report", body: { target_id: report.user_id, reason: v }, msg: "Report sent. Thank you." })}
                  className="w-full rounded-xl border border-border px-3 py-2 text-left text-sm hover:border-rose-500">{l}</button>
              ))}
            </div>
            <button onClick={() => setReport(null)} className="mt-3 w-full rounded-full py-2 text-sm text-muted-foreground">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return <button data-testid={testid} title={title} onClick={onClick} className="flex h-9 w-9 items-center justify-center rounded-full text-rose-500 transition-colors hover:bg-rose-500/10">{children}</button>;
}function MenuItem({ children, icon: Icon, onClick, danger, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary ${danger ? "text-destructive" : ""}`}><Icon size={16} weight="fill" /> {children}</button>;
}

function fmtWhen(iso) {
  try { return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function DateScheduler({ match, onClose }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const { data } = useQuery({ queryKey: ["match-dates", match.user_id], queryFn: async () => (await api.get(`/match/dates?other_id=${match.user_id}`)).data });
  const dates = data?.dates || [];
  const act = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["match-dates", match.user_id] }),
    onError: (e) => toast.error(apiError(e)),
  });
  const propose = () => {
    if (!when) { toast.error("Pick a date & time"); return; }
    act.mutate({ path: "/match/date/propose", body: { other_id: match.user_id, when_iso: new Date(when).toISOString(), note } },
      { onSuccess: () => { toast.success("Date invite sent"); setWhen(""); setNote(""); } });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-card p-5" onClick={(e) => e.stopPropagation()} data-testid="match-date-modal">
        <h3 className="font-display text-lg font-semibold">Plan a virtual date with {match.display_name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">Pick a time — we'll open a private video room and remind you both.</p>
        <div className="mt-3 space-y-2">
          <input data-testid="match-date-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-rose-500" />
          <input data-testid="match-date-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)"
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-rose-500" />
          <button data-testid="match-date-send" onClick={propose} disabled={act.isPending}
            className="w-full rounded-full bg-rose-500 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Send date invite</button>
        </div>
        {dates.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="match-date-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Planned</p>
            {dates.map((d) => (
              <div key={d.id} className="rounded-xl border border-border p-3 text-sm" data-testid={`match-date-${d.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{fmtWhen(d.when_iso)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${d.status === "accepted" ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>{d.status}</span>
                </div>
                {d.note && <p className="mt-1 text-xs text-muted-foreground">{d.note}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.status === "proposed" && d.you_are === "invitee" && (<>
                    <button data-testid={`date-accept-${d.id}`} onClick={() => act.mutate({ path: "/match/date/respond", body: { id: d.id, accept: true } }, { onSuccess: () => toast.success("Date confirmed") })}
                      className="rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-white">Accept</button>
                    <button data-testid={`date-decline-${d.id}`} onClick={() => act.mutate({ path: "/match/date/respond", body: { id: d.id, accept: false } })}
                      className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold">Decline</button>
                  </>)}
                  {d.status === "proposed" && d.you_are === "proposer" && <span className="text-xs text-muted-foreground">Waiting for a reply…</span>}
                  {d.status === "accepted" && d.room_code && (
                    <button data-testid={`date-join-${d.id}`} onClick={() => nav(`/app/meet?room=${d.room_code}`)}
                      className="rounded-full bg-rose-500 px-4 py-1.5 text-xs font-semibold text-white">Join date</button>
                  )}
                  <button data-testid={`date-cancel-${d.id}`} onClick={() => act.mutate({ path: "/match/date/cancel", body: { id: d.id } })}
                    className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold text-muted-foreground">Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button onClick={onClose} className="mt-3 w-full rounded-full py-2 text-sm text-muted-foreground">Close</button>
      </div>
    </div>
  );
}
